import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Injector,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { LoggerService } from "@bfx/logger";
import {
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
  NumberSpinnerComponent,
  SpinType,
  UpdateSourceType,
  ZERO,
} from "shared/base-components/number-spinner.component";
import { BigNumber, CurrencyPairImpl, RequestTrackingType } from "shared/cubiq-model-multi-asset";
import { CcyPairLookupService, CcyPairPromiseLookupService } from "shared/cubiq-services";
import { BfxInputDirective } from "./bfx-input";
import { BfxFieldNumberDirective } from "./bfx-field-number";
import { RateSpinnerPairConstants } from "./rate-spinner.constants";

export const SPINNER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RateSpinnerComponent),
  multi: true,
};

const logger = LoggerService.for(__dirname);

/**
 * Number stepper component for entering rate values - there is no default handling for amount quantities, and
 * precision and pip sizes are looked up in a system service on entry of a pair code.
 * On focus, the pip precision is automatically highlighted
 */
@Component({
  selector: "rate-spinner",
  templateUrl: "./html/number-spinner.html",
  providers: [SPINNER_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RateSpinnerComponent extends NumberSpinnerComponent {
  private _isFwd: boolean = false;

  public get isFwd(): boolean {
    return this._isFwd;
  }

  @Input()
  public set isFwd(value: boolean) {
    this._isFwd = value;
  }

  @Input("trigger") trigger: RequestTrackingType;

  protected isInverted: boolean = false;

  private _productCode: string = null;

  protected rateDigits: number = null;
  protected pointsDigits: number = null;

  protected rateStep: BigNumber = null;
  protected pointsStep: BigNumber = null;

  /** Multiplier for converting pips to points */
  protected pipsToPoints: BigNumber = null;

  /** Multiplier for converting points to pips */
  protected pointsToPips: BigNumber = null;

  @Output()
  public pairConstants: EventEmitter<RateSpinnerPairConstants> = new EventEmitter();

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
    protected currencyPairService: CcyPairPromiseLookupService,
  ) {
    super(injector, inputDirective, fieldDirective);
    this.maxPrecision = 4;
    this.minPrecision = 4;
    this.spinType = SpinType.PIP;
    this.min = ZERO;
    this.step = new BigNumber("0.0001");
    this.minMaxTouchValid = false;
    this.keyupAssignZero = true;
    // For rates, we don't want to have commans for very large rates
    this.groupNonDecimals = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (
      Object.hasOwn(changes, "inverted") ||
      Object.hasOwn(changes, "productCode") ||
      Object.hasOwn(changes, "isFwd") ||
      Object.hasOwn(changes, "trigger")
    ) {
      this.updateProductCode(this._productCode, this.isFwd);
    }
  }

  /**
   * Calculates the request price spinner step based
   * on the selected currency pair. If no currency pair is selected
   * the method returns a default value of 1.
   */
  public getStep(pair: CurrencyPairImpl): BigNumber {
    let step: BigNumber;
    if (pair != null) {
      let powValue: number;
      if (this.isFwdTracked()) {
        let fwdPips: number = this.isInverted ? pair.fwdInvertPipsQty : pair.fwdPipsQty;
        powValue = -fwdPips;
      } else {
        let spotPips: number = this.isInverted ? pair.spotInvertDecimalsQty : pair.rateDecimalsQty;
        powValue = -spotPips;
      }
      step = new BigNumber("10").pow(powValue);
    } else {
      step = new BigNumber(1);
    }

    return step;
  }

  protected override highlightOnFocus(element: HTMLInputElement): void {
    if (this.config.pipSize > 0) {
      let text: string = this.inputField.nativeElement.value;
      let decIdx = text.indexOf(".");
      element.setSelectionRange(decIdx + 1 + Math.max(0, this.config.pipSize - 2), text.length);
    } else {
      super.highlightOnFocus(element);
    }
  }

  // protected override getModelValue(): any {
  //   return new BfxRequestPriceModel(this.data.value, this.data.textValue);
  // }

  writeValue(_value: BigNumber): void {
    if (_value != null && this._productCode != null) {
      // if an external value is set with greater precision, we need to round it to what is displayed
      _value = new BigNumber(_value.toFixed(this.config.maxPrecision));
    }
    super.writeValue(_value);
  }

  @Input() public set inverted(value: boolean) {
    // pair lookup is done in onChanges to minimise no of lookups
    this.isInverted = value;
  }

  public get inverted() {
    return this.isInverted;
  }

  public get productCode(): string {
    return this._productCode;
  }

  @Input()
  public set productCode(productCode: string) {
    // pair lookup is done in onChanges to minimise no of lookups
    this._productCode = productCode;
  }

  protected updateProductCode(productCode: string, isFwd: boolean) {
    this.isFwd = isFwd;

    if (productCode != null && typeof productCode === "string") {
      let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
      let pair: CurrencyPairImpl = currencyPairLookup.getItem(productCode.toUpperCase());
      // this.isInverted = false;
      try {
        if (pair == null && productCode.length == 6) {
          let product: string = productCode.substring(3, 6) + productCode.substring(0, 3);
          pair = currencyPairLookup.getItem(product.toUpperCase());
          // this.isInverted = true;
        }

        if (pair != null) {
          this.precision = this.getMaxPrecision(pair);
          this.calcPairConstants(pair);
        }
      } catch (ex) {
        logger.out.error(`ERROR: _icPriceSpinner.setProductCode(): ex:`, ex);
        this.maxPrecision = 4;
        this.minPrecision = 4;
        // this.markupDecimals = 4;
        // this.fwdspotDecimals = 4;
        this.pipSize = 0;
      }

      if (pair != null) {
        this.step = this.getStep(pair);
      }

      this.updateConfiguration();
      this.pairConstants.emit({
        pipSize: this.pipSize,
        maxPrecision: this.maxPrecision,
        minPrecision: this.minPrecision,
        step: this.step,
        pointsDigits: this.pointsDigits,
        rateDigits: this.rateDigits,
        pipsToPoints: this.pipsToPoints,
        pointsToPips: this.pointsToPips,
      });
      this.updateValue(this.data.value, UpdateSourceType.CONFIG_CHANGE);
      this.formatInputValue();
    }
  }

  /**
   * Work out pip size and other pair constants
   * @param pair
   */
  protected calcPairConstants(pair: CurrencyPairImpl) {
    let maximumPrecision: number = 4;
    this.config.pipSize = 4;
    if (this.isFwdTracked()) {
      if (this.isInverted) {
        maximumPrecision = Math.max(
          pair.fwdInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
        this.pipSize = pair.fwdInvertPipsQty;
      } else {
        maximumPrecision = Math.max(
          pair.fwdRateDecimalsQty,
          pair.markupSptDecimals,
          pair.limitEntrySpotDecimals,
        );
        this.pipSize = pair.fwdPipsQty;
      }
    } else {
      if (this.isInverted) {
        maximumPrecision = Math.max(
          pair.spotInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
        this.pipSize = pair.spotInvertDecimalsQty; // NB TODO = no Invert spot pips on the pair ??
      } else {
        maximumPrecision = Math.max(
          pair.rateDecimalsQty,
          pair.markupSptDecimals,
          pair.limitEntrySpotDecimals,
        );
        this.pipSize = pair.rateDecimalsQty;
      }
    }
    this.pointsDigits = maximumPrecision - this.pipSize;
    this.rateDigits = maximumPrecision;
    this.pipsToPoints = new BigNumber("10").pow(-this.pipSize);
    this.pointsToPips = new BigNumber("10").pow(this.pipSize);
  }

  /**
   * Determine the maximum display precision
   * @param pair
   * @returns
   */
  protected getMaxPrecision(pair: CurrencyPairImpl): number {
    let digits = 4;
    if (pair) {
      if (this.isFwdTracked()) {
        if (this.isInverted) {
          digits = pair.fwdInvertDecimalsQty;
        } else {
          digits = pair.fwdRateDecimalsQty;
        }
      } else {
        if (this.isInverted) {
          digits = pair.limitEntrySpotInvertDecimals;
        } else {
          digits = pair.limitEntrySpotDecimals;
        }
      }
    }
    if (digits == null || isNaN(digits)) {
      logger.out.warn(
        `Misconfigured pair - got null decimals for ${pair.base + pair.nonBase} - fwd = ${
          this.isFwd
        }, invert=${this.isInverted}`,
      );
      digits = 0;
    }
    return digits;
  }

  /**
   * Retain for compatibility with old spinners
   * @deprecated VAlidation should no longer need to be called explicitly - but updateValidState() has now superceded this.
   */
  public validateValue(): void {
    this.updateValidState();
  }

  private isFwdTracked(): boolean {
    return (
      this.isFwd && (RequestTrackingType.OUTRIGHT_RATE === this.trigger || this.trigger == null)
    );
  }
}
